import {X, Download} from 'lucide-react'
import { QUALITY_RANGES, type Unit, type LiquidType, type LyeType, type SoapCalculations } from "@/data/oilDatabase"
import { exportToPDF } from "@/utils/exportPdf"
import { barWeightForUnit, calculatePricing, type PricingInputs } from "@/utils/pricing"

interface PrintableRecipeModalProps {
  calculations: SoapCalculations
  unit: Unit
  batchSize: number
  superfat: number
  lyeType: LyeType
  liquidType: LiquidType
  fragrancePercent: number
  pricing?: PricingInputs
  onClose: () => void
}

export function PrintableRecipeModal({
  calculations,
  unit,
  batchSize,
  superfat,
  lyeType,
  liquidType,
  fragrancePercent,
  pricing,
  onClose,
}: PrintableRecipeModalProps) {
  const pricingResult = pricing
    ? calculatePricing(calculations.totalCost, calculations.totalBatchWeight, unit, pricing)
    : null
  const oilsWithCost = calculations.oilsWithCalculatedWeights.filter((o) => o.costPerUnit > 0)

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-auto">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-2xl font-bold text-gray-900">Soap Recipe Card</h3>
            <div className="flex gap-2">
              <button
                onClick={() =>
                  exportToPDF({ calculations, unit, batchSize, superfat, lyeType, liquidType, fragrancePercent, pricing })
                }
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
              >
                <Download size={18} />
                Export PDF
              </button>
              <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg">
                <X size={24} />
              </button>
            </div>
          </div>

          {/* Recipe Settings */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-6">
            <h4 className="font-bold text-gray-900 mb-3">Recipe Settings</h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>Batch Size: <span className="font-semibold">{batchSize} {unit}</span></div>
              <div>Superfat: <span className="font-semibold">{superfat}%</span></div>
              <div>Lye Type: <span className="font-semibold">{lyeType}</span></div>
              <div>Liquid: <span className="font-semibold capitalize">{liquidType.replace("-", " ")}</span></div>
            </div>
          </div>

          {/* Oil Blend */}
          <div className="mb-6">
            <h4 className="font-bold text-gray-900 mb-3">Oil Blend</h4>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2">Oil</th>
                  <th className="text-center py-2">%</th>
                  <th className="text-right py-2">Weight</th>
                </tr>
              </thead>
              <tbody>
                {calculations.oilsWithCalculatedWeights.map((oil, i) => (
                  <tr key={i} className="border-b border-gray-100">
                    <td className="py-2">{oil.oil}</td>
                    <td className="text-center py-2">{oil.percentage.toFixed(1)}%</td>
                    <td className="text-right py-2">{oil.weight.toFixed(1)} {unit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Required Ingredients */}
          <div className="bg-gray-50 rounded-xl p-4 mb-6">
            <h4 className="font-bold text-gray-900 mb-3">Required Ingredients</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Total Oils:</span>
                <span className="font-semibold">{calculations.totalOilWeight.toFixed(1)} {unit}</span>
              </div>
              <div className="flex justify-between">
                <span>{lyeType}:</span>
                <span className="font-semibold">{calculations.totalLyeRequired.toFixed(1)} {unit}</span>
              </div>
              <div className="flex justify-between">
                <span>Liquid ({liquidType.replace("-", " ")}):</span>
                <span className="font-semibold">{calculations.totalLiquid.toFixed(1)} {unit}</span>
              </div>
              <div className="flex justify-between">
                <span>Fragrance ({fragrancePercent}%):</span>
                <span className="font-semibold">{calculations.fragranceLoad.toFixed(1)} {unit}</span>
              </div>
              <div className="flex justify-between pt-2 border-t border-gray-300 font-bold">
                <span>Total Batch Weight:</span>
                <span className="text-emerald-600">{calculations.totalBatchWeight.toFixed(1)} {unit}</span>
              </div>
              {calculations.totalCost > 0 && (
                <div className="flex justify-between pt-2 border-t border-gray-300 font-bold">
                  <span>Total Oil Cost:</span>
                  <span>${calculations.totalCost.toFixed(2)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Cost & Pricing */}
          {(oilsWithCost.length > 0 || pricingResult) && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-6">
              <h4 className="font-bold text-gray-900 mb-3">Cost Breakdown &amp; Pricing</h4>

              {oilsWithCost.length > 0 && (
                <div className="space-y-1 text-sm mb-3">
                  {oilsWithCost.map((oil, i) => (
                    <div key={i} className="flex justify-between">
                      <span className="text-gray-700">{oil.oil}</span>
                      <span className="font-medium">${(oil.weight * oil.costPerUnit).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              )}

              {pricingResult && (
                <div className="space-y-1 text-sm border-t border-emerald-200 pt-3">
                  <div className="flex justify-between">
                    <span>Extra per batch (lye, fragrance, packaging):</span>
                    <span className="font-medium">${Number(pricing?.extraCostPerBatch || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Total batch cost:</span>
                    <span className="font-medium">${pricingResult.batchTotalCost.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Bar weight:</span>
                    <span className="font-medium">
                      {barWeightForUnit(pricing!.barWeightGrams, unit)} {unit}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Bars per batch:</span>
                    <span className="font-medium">
                      {Number.isFinite(pricingResult.barsPerBatch) ? pricingResult.barsPerBatch.toFixed(1) : "—"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Cost per bar:</span>
                    <span className="font-medium">${pricingResult.costPerBar.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Target margin:</span>
                    <span className="font-medium">{pricingResult.targetMargin}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Profit per bar:</span>
                    <span className="font-medium">${pricingResult.profitPerBar.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Suggested rounded price:</span>
                    <span className="font-medium">${pricingResult.roundedPrice.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Wholesale (50% of retail):</span>
                    <span className="font-medium">${pricingResult.wholesalePrice.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-emerald-300 font-bold">
                    <span>Recommended retail price:</span>
                    <span className="text-emerald-700">${pricingResult.recommendedPrice.toFixed(2)} per bar</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Quality Metrics */}
          <div className="mb-6">
            <h4 className="font-bold text-gray-900 mb-3">Quality Metrics</h4>
            <div className="grid grid-cols-2 gap-3 text-sm">
              {[
                { label: "Hardness", value: calculations.hardness, range: QUALITY_RANGES.hardness },
                { label: "Cleansing", value: calculations.cleansing, range: QUALITY_RANGES.cleansing },
                { label: "Conditioning", value: calculations.conditioning, range: QUALITY_RANGES.conditioning },
                { label: "Bubbly Lather", value: calculations.bubbly, range: QUALITY_RANGES.bubbly },
                { label: "Creamy Lather", value: calculations.creamy, range: QUALITY_RANGES.creamy },
              ].map((m) => (
                <div key={m.label} className="bg-gray-50 rounded-lg p-2">
                  <span className="text-gray-600">{m.label}:</span>
                  <span className="font-semibold ml-2">{m.value.toFixed(0)}</span>
                  <span className="text-xs text-gray-500 ml-1">({m.range.min}-{m.range.max})</span>
                </div>
              ))}
            </div>
          </div>

          {/* Checklist */}
          <div>
            <h4 className="font-bold text-gray-900 mb-3">Soap Making Checklist</h4>
            <div className="space-y-2 text-sm">
              {[
                "Weigh all oils and melt solid oils",
                "Weigh liquid in heat-safe container",
                "Carefully add lye to liquid (not liquid to lye!)",
                "Allow lye solution to cool to 100-110°F",
                "Combine oils and lye solution",
                "Blend to trace",
                "Add fragrance and mix thoroughly",
                "Pour into mold",
                "Insulate for 24 hours",
                "Unmold and cut after 24-48 hours",
                "Cure for 4-6 weeks",
              ].map((item, i) => (
                <label key={i} className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500" />
                  <span>{item}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
